import React, { useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useParams } from "react-router";
import { useFormikContext } from "formik";
import { Box, HStack, VStack } from "../../../components/utils";
import { TransactionDetailsData } from "../../../types/Transaction";
import { MonetaryEventState } from "../../../types/OptionBuyback";

type Step = {
  stepNo: number;
  title: string;
  description: string;
  children?: Step[];
};
type StepProps = {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
};
export default function ExerciseDetailsStepper(props: StepProps) {
  const currentStep = useMemo(() => props.currentStep, [props.currentStep]);
  const formik = useFormikContext<TransactionDetailsData>();
  const location = useLocation();
  const params = useParams();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;
  useEffect(() => {
    if (mode === "edit") {
      const state = formik.values.eventState;
      if (state) {
        if (state === MonetaryEventState.DRAFT) {
          if (formik.values.monetaryDetails?.considerBlackOutDays) {
            props.setCurrentStep(3);
          } else if (
            formik.values.monetaryDetails.participantListMethod ===
            "Upload Excel"
          ) {
            props.setCurrentStep(5);
          } else {
            props.setCurrentStep(4);
          }
        } else if (state === MonetaryEventState.STARTED) {
          props.setCurrentStep(6);
        } else if (state === MonetaryEventState.CLOSED) {
          props.setCurrentStep(7);
        } else if (state === MonetaryEventState.COMPLETED) {
          props.setCurrentStep(8);
        }
      }
    }
  }, [formik.values.eventState]);
  const steps = useMemo(() => {
    const returnData: Step[] = [
      {
        stepNo: 1,
        title: "Basic Details",
        description: "Create and define a new event",
      },
      {
        stepNo: 2,
        title: "Exercise Event Configuration",
        description: "Specify event details",
        children: [
          {
            stepNo: 2.1,
            title: "Workflow Configuration",
            description: "Arrange the order of employee steps",
          },
        ],
      },
      {
        stepNo: 3,
        title: "Blackout Days",
        description: "Define blackout days for the employees",
      },
      {
        stepNo: 4,
        title: "Participant List",
        description: "Review participants list for the event",
      },
      {
        stepNo: 5,
        title: "Preview",
        description: "Review and finalize event details",
      },
      {
        stepNo: 6,
        title: "Transaction Started",
        description: "Transaction started, participants can now exercise",
      },
      {
        stepNo: 7,
        title: "Share Issuance",
        description: "Shares issued to participants",
      },
      {
        stepNo: 8,
        title: "Summary",
        description: "View the summary of the event",
      },
    ];
    return returnData;
  }, []);
  return (
    <Box className="h-full">
      {steps.map((step) => (
        <StepContainer
          key={step.stepNo}
          step={step}
          completed={
            parseInt(`${currentStep}`, 10) > parseInt(`${step.stepNo}`, 10)
          }
          current={
            parseInt(`${currentStep}`, 10) === parseInt(`${step.stepNo}`, 10)
          }
          currentStep={currentStep}
          setCurrentStep={props.setCurrentStep}
          formik={formik}
        />
      ))}
    </Box>
  );
}

const StepContainer = (props: {
  step: Step;
  current?: boolean;
  completed?: boolean;
  nextStepsAvailable?: boolean;
  currentStep?: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  _className?: string;
  formik?: any;
}) => {
  const {
    step,
    current,
    completed,
    currentStep,
    setCurrentStep,
    _className,
    formik,
  } = props;
  const { mode } = useParams();
  function handleStepClick(
    step: Step,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void {
    event.stopPropagation();
    if (mode !== "edit") return;
    if (formik.values.eventState === MonetaryEventState.DRAFT) {
      return;
    } else if (
      formik.values.eventState === MonetaryEventState.STARTED &&
      step.stepNo > 7
    ) {
      return;
    } else if (
      formik.values.eventState === MonetaryEventState.CLOSED &&
      step.stepNo > 8
    ) {
      return;
    }
    setCurrentStep(step.stepNo);
  }
  return (
    <HStack
      className={`w-full cursor-pointer pt-7 ${
        !_className ? "pl-6" : _className
      }`}
    >
      <VStack
        className={`justify-between w-1/5 ${
          mode === "edit" &&
          formik?.values.eventState !== MonetaryEventState.DRAFT &&
          (step.stepNo <= 6 ||
            (formik?.values.eventState === MonetaryEventState.CLOSED &&
              step.stepNo <= 7) ||
            (formik?.values.eventState === MonetaryEventState.COMPLETED &&
              step.stepNo <= 8))
            ? "cursor-pointer"
            : ""
        }`}
        onClick={(_) => handleStepClick(step, _)}
      >
        <div
          className={`rounded-md shadow-md  ${
            current
              ? " text-white bg-orange-501  "
              : "bg-opacity-50 text-orange-501"
          }  w-10 h-10 inline-flex justify-center items-center font-medium`}
        >
          {completed ? (
            <Icon icon="akar-icons:check" className="text-orange-501" />
          ) : (
            step.stepNo
          )}
        </div>
      </VStack>
      <VStack
        className="w-4/5 justify-evenly"
        onClick={(_) => handleStepClick(step, _)}
      >
        <div
          className={`text-sm2 font-medium text-black-501 ${
            current && "text-orange-501"
          } `}
        >
          {step.title}
        </div>
        <div
          className={`text-xs0 text-gray-401 font-medium`}
          onClick={(_) => handleStepClick(step, _)}
        >
          {step.description}
        </div>
        {step.children && (
          <div className="">
            {step.children.map((child) => (
              <StepContainer
                key={child.stepNo}
                step={child}
                current={currentStep === child.stepNo}
                completed={(currentStep || 0) > child.stepNo}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                _className="pl-0"
                formik={formik}
              />
            ))}
          </div>
        )}
      </VStack>
    </HStack>
  );
};
